import { Controller } from "@hotwired/stimulus"

/**
 * Narration Generator Controller
 *
 * Drives the narration (voiceover) wizard step for the composable kit-template
 * engine's narration aspect (ShortsWizard::NarrationStepComponent). Reads the
 * selected voice + script from the step's own inputs, POSTs to the aspect's
 * generate_narration member action, then fills the hidden audio_url/duration
 * fields (so the next wizard save persists them) and shows an <audio> preview.
 *
 * Server contract (Narration::GenerateNarrationService via the aspect):
 *   POST url  { voice_profile_id, text }
 *   → { success, audio_url, duration }  |  { success:false, error }
 */
export default class extends Controller {
  static targets = [
    "voice",
    "script",
    "generateButton",
    "status",
    "audio",
    "audioField",
    "durationField",
    "audioSection",
  ]

  static values = { url: String }

  connect() {
    this.isGenerating = false
  }

  async generate() {
    if (this.isGenerating) return

    const text = (this.hasScriptTarget ? this.scriptTarget.value : "").trim()
    if (!text) {
      this.showStatus("Add some script text first", "error")
      return
    }
    if (text.length > 5000) {
      this.showStatus("Script exceeds the 5000 character limit", "error")
      return
    }

    const voiceProfileId = this.hasVoiceTarget ? this.voiceTarget.value : ""
    if (!voiceProfileId) {
      this.showStatus("Pick a voice first", "error")
      return
    }

    this.isGenerating = true
    if (this.hasGenerateButtonTarget) this.generateButtonTarget.disabled = true
    this.showStatus("Generating…", "loading")

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ voice_profile_id: voiceProfileId, text }),
      })
      const data = await response.json()

      if (response.ok && data.success) {
        this.applyResult(data)
        this.showStatus("Voiceover ready", "success")
      } else {
        this.showStatus(data.error || "Generation failed", "error")
      }
    } catch (error) {
      this.showStatus("Network error", "error")
    } finally {
      this.isGenerating = false
      if (this.hasGenerateButtonTarget) this.generateButtonTarget.disabled = false
    }
  }

  applyResult(data) {
    if (this.hasAudioFieldTarget) this.audioFieldTarget.value = data.audio_url || ""
    if (this.hasDurationFieldTarget) this.durationFieldTarget.value = data.duration ?? ""
    if (this.hasAudioTarget && data.audio_url) {
      this.audioTarget.src = data.audio_url
    }
    if (this.hasAudioSectionTarget && data.audio_url) {
      this.audioSectionTarget.style.display = ""
    }
  }

  showStatus(message, type = "info") {
    if (!this.hasStatusTarget) return
    const colors = {
      loading: "#6366f1",
      success: "#16a34a",
      error: "#dc2626",
      info: "#6b7280",
    }
    this.statusTarget.textContent = message
    this.statusTarget.style.color = colors[type] || colors.info
  }
}
